(() => {
  class SncPresentationScroll {
    constructor(root) {
      this.root = root;
      this.steps = Array.from(root.querySelectorAll('[data-snc-step]'));
      this.media = Array.from(root.querySelectorAll('[data-snc-media]'));
      this.stepsTrack = root.querySelector('.snc-scroll__steps');
      this.mobileStage = null;
      this.mobileStageItems = [];
      this.activeIndex = -1;
      this.ticking = false;
      this.mobileTicking = false;

      if (!this.steps.length) return;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMobileScroll = this.onMobileScroll.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.buildMobileStage();

      this.root.__sncPresentationScroll = this;

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      if (this.stepsTrack) {
        this.stepsTrack.addEventListener('scroll', this.onMobileScroll, { passive: true });
      }
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.setActive(0, true);
      this.update();
    }

    buildMobileStage() {
      const existingStage = this.root.querySelector('.snc-scroll__mobile-stage');
      if (existingStage) existingStage.remove();

      if (!this.stepsTrack) return;

      const stage = document.createElement('div');
      stage.className = 'snc-scroll__mobile-stage';
      stage.setAttribute('aria-hidden', 'true');

      this.steps.forEach((step, index) => {
        const source = step.querySelector('.snc-scroll__mobile-media');
        const sourceVisual = source && source.firstElementChild;
        if (!sourceVisual) return;

        const item = document.createElement('figure');
        item.className = `snc-scroll__mobile-stage-item${index === 0 ? ' is-active' : ''}`;
        item.dataset.index = String(index);

        const visual = sourceVisual.cloneNode(true);
        if (visual.id) visual.removeAttribute('id');
        item.appendChild(visual);
        stage.appendChild(item);
      });

      if (!stage.children.length) return;

      this.stepsTrack.parentNode.insertBefore(stage, this.stepsTrack);
      this.mobileStage = stage;
      this.mobileStageItems = Array.from(stage.querySelectorAll('.snc-scroll__mobile-stage-item'));
    }

    onScroll() {
      if (this.ticking) return;

      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    onMobileScroll() {
      if (this.mobileTicking) return;

      this.mobileTicking = true;
      window.requestAnimationFrame(() => {
        if (window.matchMedia('(max-width: 749px)').matches) {
          this.updateMobile();
        }
        this.mobileTicking = false;
      });
    }

    onResize() {
      this.onScroll();
      this.onMobileScroll();
    }

    update() {
      if (window.matchMedia('(max-width: 749px)').matches) {
        this.updateMobile();
        return;
      }

      if (window.matchMedia('(max-width: 989px)').matches) {
        return;
      }

      const triggerLine = window.innerHeight * 0.52;
      let nextIndex = 0;

      this.steps.forEach((step, index) => {
        const rect = step.getBoundingClientRect();
        if (rect.top <= triggerLine) {
          nextIndex = index;
        }
      });

      this.setActive(nextIndex);
    }

    updateMobile() {
      if (!this.stepsTrack || !this.steps.length) return;

      const trackRect = this.stepsTrack.getBoundingClientRect();
      let nextIndex = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      this.steps.forEach((step, index) => {
        const stepRect = step.getBoundingClientRect();
        const distance = Math.abs(stepRect.left - trackRect.left);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          nextIndex = index;
        }
      });

      this.setActive(nextIndex);
    }

    setActive(index, force = false) {
      const safeIndex = Math.max(0, Math.min(index, this.steps.length - 1));
      if (!force && safeIndex === this.activeIndex) return;

      this.activeIndex = safeIndex;
      this.root.dataset.activeIndex = String(safeIndex);

      this.steps.forEach((step, stepIndex) => {
        const isActive = stepIndex === safeIndex;
        step.classList.toggle('is-active', isActive);
        step.setAttribute('aria-current', isActive ? 'step' : 'false');
      });

      this.media.forEach((item) => {
        const mediaIndex = Number(item.dataset.index || 0);
        const isActive = mediaIndex === safeIndex;

        item.classList.toggle('is-active', isActive);
        item.classList.toggle('is-before', mediaIndex < safeIndex);
        item.classList.toggle('is-after', mediaIndex > safeIndex);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.mobileStageItems.forEach((item) => {
        const mediaIndex = Number(item.dataset.index || 0);
        item.classList.toggle('is-active', mediaIndex === safeIndex);
      });
    }

    onBlockSelect(event) {
      const step = event.target.closest('[data-snc-step]');
      if (!step || !this.root.contains(step)) return;

      const index = Number(step.dataset.index || 0);
      this.setActive(index, true);

      if (window.innerWidth >= 990) {
        step.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (window.innerWidth <= 749 && this.stepsTrack) {
        this.stepsTrack.scrollTo({
          left: step.offsetLeft,
          behavior: 'smooth'
        });
      }
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.stepsTrack) {
        this.stepsTrack.removeEventListener('scroll', this.onMobileScroll);
      }
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      delete this.root.__sncPresentationScroll;
    }
  }

  const init = (scope = document) => {
    const roots = scope.matches && scope.matches('[data-snc-scroll]')
      ? [scope]
      : Array.from(scope.querySelectorAll('[data-snc-scroll]'));

    roots.forEach((root) => {
      if (!root.__sncPresentationScroll) {
        new SncPresentationScroll(root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', () => {
    document.querySelectorAll('[data-snc-scroll]').forEach((root) => {
      if (root.__sncPresentationScroll) root.__sncPresentationScroll.update();
    });
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches('[data-snc-scroll]')
      ? event.target
      : event.target.querySelector('[data-snc-scroll]');

    if (root && root.__sncPresentationScroll) {
      root.__sncPresentationScroll.destroy();
    }
  });
})();
